import { jsx as _jsx } from "react/jsx-runtime";
import Co2 from "@assets/icon-co2_cloud.svg";
import DirtHand from "@assets/icon-dirt_hand.svg";
import DumbSeagull from "@assets/icon-dumb_seagull.svg";
import MixedForest from "@assets/icon-mixed_forest.svg";
import Kelp from "@assets/icon-ocean_kelp.svg";
import { impacts } from "./processedTypes";
export const descriptionMap = {
    treeTypes: {
        Trees: {
            primary: "Total Trees",
            secondary: "Compares to planting ${0} studio apartments full of trees",
            icon: _jsx(DirtHand, {}),
            countKey: "trees_ordered",
            valueFiller: (_, trees) => trees[1].trees_ordered / 11.22,
        },
    },
    impacts: {
        co2: {
            primary: "Tonnes of CO2 to be sequestered",
            secondary: "Compares to removing ${0} cars from the road for one year",
            icon: _jsx(Co2, {}),
            countKey: "co2_ordered_projected_stat",
            valueFiller: (raw) => (raw.co2_ordered_projected_stat || 0) / 4.598,
        },
        areaPlanted: {
            icon: _jsx(MixedForest, {}),
            primary: "Hectares Reforested",
            secondary: "Provides ${0} work days to local tree planters.",
            countKey: "area_planted_ordered_projected_stat",
            valueFiller: (raw) => raw.working_days_projected_stat || 0,
        },
        yield: {
            primary: "Estimated Yield",
            secondary: "Equivalent to the weight of ${0} seagulls",
            icon: _jsx(DumbSeagull, {}),
            countKey: "yield_ordered_projected_stat",
            units: "kg",
            valueFiller: (raw) => (raw.yield_ordered_projected_stat || 0) / 4,
        },
        oceanAreaPlanted: {
            primary: `Ocean Area Restored`,
            secondary: `Equivalent to the area approximately the size of ${0} parking space`,
            icon: _jsx(Kelp, {}),
            countKey: "ocean_area_planted_ordered_projected_stat",
            primaryConversion: (value) => value * 10000,
            units: "m²",
            valueFiller: (raw) => ((raw.ocean_area_planted_ordered_projected_stat || 0) * 10000) / 4.55,
        },
    },
};
/**
 * Converts the raw impacts data into a more usable format.
 *
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted impacts data.
 */
export function convertImpacts(raw, trees_by_forest_types) {
    const base = [];
    for (const forestType in descriptionMap.treeTypes) {
        const treeTypeData = processTreeType(forestType, raw, trees_by_forest_types);
        if (!treeTypeData)
            continue;
        base.push(treeTypeData);
    }
    for (const impact in descriptionMap.impacts) {
        const impactData = processImpact(impact, raw, trees_by_forest_types);
        if (!impactData)
            continue;
        base.push(impactData);
    }
    return base;
}
/**
 * Converts a tree type into a more usable format.
 *
 * @param forestType The forest type to convert.
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted tree type data.
 */
function processTreeType(forestType, raw, trees_by_forest_types) {
    const supplementary = descriptionMap.treeTypes[forestType];
    if (!supplementary)
        return null;
    const forest_type = trees_by_forest_types.find((forest) => forest.type === forestType);
    if (!forest_type)
        return null;
    let count = forest_type[supplementary.countKey] || 0;
    const { count: newCount, secondary, secondaryValue, } = adjustCountsAndText(count, supplementary, raw, trees_by_forest_types);
    count = newCount;
    return Object.assign(Object.assign({}, supplementary), { secondary, slug: `${forest_type.forest_type_id}`, count,
        secondaryValue });
}
/**
 * Converts a raw impact into a more usable format.
 *
 * @param impact The impact to convert.
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted impact data.
 */
function processImpact(impact, raw, trees_by_forest_types) {
    const slug = impact;
    const rawKey = impacts[slug];
    const supplementary = descriptionMap.impacts[slug];
    if (!supplementary) {
        return null;
    }
    const rawObj = raw[rawKey];
    if (!rawObj) {
        return null;
    }
    let count = raw[supplementary.countKey] || 0;
    const { count: newCount, secondary, secondaryValue, } = adjustCountsAndText(count, supplementary, raw, trees_by_forest_types);
    count = newCount;
    return Object.assign(Object.assign({}, supplementary), { secondary,
        slug,
        count,
        secondaryValue });
}
/**
 * Adjusts the counts and text for a given impact.
 *
 * @param rawCount The raw count.
 * @param supplementary The supplementary data for the impact.
 * @param impactData The impact data.
 * @param overallData The overall data.
 * @returns The adjusted counts and text.
 */
function adjustCountsAndText(rawCount, supplementary, impactData, overallData) {
    let count = rawCount;
    if (supplementary.primaryConversion) {
        count = supplementary.primaryConversion(count);
    }
    const secondaryValue = supplementary.valueFiller(impactData, overallData);
    return {
        count,
        secondary: supplementary.secondary,
        secondaryValue: secondaryValue.toLocaleString(),
    };
}
